'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { useModalController } from '@uikit';

import { INTERNAL_VOTING_TYPE } from '@shared/api/types';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { formatMillisecondsToDateWithApostrophe } from '@shared/helpers/date';
import { getSnapshotProposalUrl } from '@shared/helpers/snapshot';
import { resolveAppUrl } from '@shared/utils/resolveAppUrl';

import {
  useAppCurrentUserMetaQuery,
  useAppQuery,
  useAppValidationQuery,
  useAppValidationVotersListInfiniteQuery,
} from '@query/app';
import { useFeatureTogglesQuery } from '@query/featureToggles';

import VotingModal from '@store:web/components/common/AppValidationVoting/VotingModal';
import {
  type ApplicationTableData,
  ApplicationValidationSummary,
  type ControlsProps,
  type DetailsProps,
  type ScoreProps,
  type VotesTableModel,
} from '@ui/uikit/components/applications/common/ApplicationValidationSummary';
import { Activity, type ActivityHandle } from '@ui/uikit/components/common/Activity';
import { openUrl } from '@utilities/browser';

import { mapVote } from './mapVote';

interface ApplicationValidationSectionProps {
  applicationSlug: string;
  appStatus?: string;
}

const ApplicationValidationSection = ({
  applicationSlug,
  appStatus,
}: ApplicationValidationSectionProps) => {
  const { user, isAuth } = useAuth();
  const { open: openAuthModal } = useAuthModal();

  const voteModal = useModalController();
  const { data: featureToggles = {} } = useFeatureTogglesQuery();
  const { data: app } = useAppQuery(applicationSlug);

  const { data: validation, isLoading: isValidationLoading } =
    useAppValidationQuery(applicationSlug);
  const { data: meta, refetch: refetchMeta } = useAppCurrentUserMetaQuery(applicationSlug, {
    enabled: isAuth,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });
  const {
    data: votersPages,
    fetchNextPage,
    hasNextPage,
    isLoading: isInitialLoading,
    isFetchingNextPage,
  } = useAppValidationVotersListInfiniteQuery({ id: applicationSlug });

  const hasVoted = isAuth && !!meta?.attributes?.voted;

  const voteUrl = resolveAppUrl(applicationSlug, appStatus, true, 'validation');

  const scoreProps: ScoreProps = useMemo(
    () => ({
      maxValue: 5,
      value: validation?.score,
      votes: validation?.count,
      entityType: 'Vote',
    }),
    [validation?.count, validation?.score],
  );

  const validationPeriod = useMemo(() => {
    const endDateMs = validation?.endDate ? new Date(validation.endDate).getTime() : 0;

    return {
      startDate: validation?.startDate,
      endDate: validation?.endDate,
      formattedEndDate: formatMillisecondsToDateWithApostrophe(endDateMs),
    };
  }, [validation?.endDate, validation?.startDate]);

  const detailsProps: DetailsProps = useMemo(
    () => ({
      voteStats: {
        approvedPercentage: validation?.result.yes,
        rejectedPercentage: validation?.result.no,
        totalVotes: validation?.isSqrvVotingPowerUsed
          ? validation?.validationPower
          : validation?.count,
      },
      validationPeriod,
      preSnapshotSqrvVotingPower: validation?.interimSqrv,
    }),
    [
      validation?.result.yes,
      validation?.result.no,
      validation?.isSqrvVotingPowerUsed,
      validation?.validationPower,
      validation?.count,
      validation?.interimSqrv,
      validationPeriod,
    ],
  );

  const selfVotesTableData: ApplicationTableData[] = useMemo(
    () => (hasVoted && validation?.currUserVote ? [mapVote(validation.currUserVote)] : []),
    [hasVoted, validation?.currUserVote],
  );

  const votesTableData: ApplicationTableData[] = useMemo(() => {
    const pages = votersPages?.pages ?? [];

    return pages.flatMap((page) => (page.votes ?? []).map(mapVote));
  }, [votersPages?.pages]);

  const { ref: loadMoreRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
  });

  const votesTableModel = useMemo<VotesTableModel>(
    () => ({
      selfData: selfVotesTableData,
      data: votesTableData,
      isInitialLoading,
      hasNextPage,
      isFetchingNextPage,
      loadMoreRef,
      votersCount: validation?.count,
    }),
    [
      selfVotesTableData,
      votesTableData,
      isInitialLoading,
      hasNextPage,
      isFetchingNextPage,
      loadMoreRef,
      validation?.count,
    ],
  );

  const activity = useRef<ActivityHandle>(null);

  const ensureAuth = useCallback(async () => {
    await activity.current?.initiate();
  }, []);

  const executeSignIn = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  const handleVote = useCallback(async () => {
    if (hasVoted) {
      return;
    }

    if (!isAuth) {
      await ensureAuth();
      const { data: freshMeta } = await refetchMeta();

      if (freshMeta?.attributes?.voted) {
        return;
      }
    }

    if (validation?.voting === INTERNAL_VOTING_TYPE.MAGICSTORE) {
      if (featureToggles.internalValidationVote) {
        voteModal.open();
      } else {
        openUrl(voteUrl);
      }

      return;
    }

    if (validation?.voting === INTERNAL_VOTING_TYPE.SNAPSHOT) {
      openUrl(getSnapshotProposalUrl(validation.id));
      return;
    }

    openUrl(voteUrl);
  }, [
    hasVoted,
    isAuth,
    validation?.voting,
    validation?.id,
    voteUrl,
    ensureAuth,
    refetchMeta,
    featureToggles.internalValidationVote,
    voteModal,
  ]);

  const handleShowDetails = useCallback(() => openUrl(voteUrl), [voteUrl]);

  const controlsProps: ControlsProps = useMemo(
    () => ({
      hasVoted,
      onVote: handleVote,
      onShowDetails: handleShowDetails,
      votesTableModel,
    }),
    [hasVoted, handleVote, handleShowDetails, votesTableModel],
  );

  return (
    <>
      <Activity ref={activity}>
        <Activity.Step requirement={!!user} action={executeSignIn} key="signin" />
      </Activity>
      <ApplicationValidationSummary
        appName={validation?.title}
        scoreProps={scoreProps}
        detailsProps={detailsProps}
        controlsProps={controlsProps}
        isValidationFinished={validation?.finished}
        isSqrvVotingPowerUsed={validation?.isSqrvVotingPowerUsed}
        isLoading={isValidationLoading}
      />
      {app && validation && featureToggles.internalValidationVote && (
        <VotingModal
          app={app}
          validationId={validation.id}
          strategy={validation.voting}
          applicationSlug={applicationSlug}
          {...voteModal.props}
        />
      )}
    </>
  );
};

export { ApplicationValidationSection };
